import { Droppable } from '@hello-pangea/dnd';
import { Add, ExpandMore } from '@mui/icons-material';
import { Button, Tooltip } from '@mui/material';
import cn from 'classnames';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';

import { Collapsed } from '@/components/Collapsed';
import { useUserStore } from '@/configs/store';
import { LogItem } from '@/entities/log/ui';
import { DetailsDrawer } from '@/features/DetailsDrawer';
import { DraftLogItem, useDraftTimelogStore } from '@/features/draftTimelogs';
import { useGlobalUserFilterModel } from '@/features/globalUserFilter';
import { modalManagerStore } from '@/features/ModalManagerStore';
import { ReadonlyGuard } from '@/features/ReadonlyGuard';
import { viewSettingsStore } from '@/features/viewSettings';
import { useDetailsDrawer } from '@/hooks/useDetailsDrawer';
import { DraftTimelog } from '@/types';
import { WeeklyWorkLog } from '@/types';
import { formatDurationRussian } from '@/utils/formatDurationRussian';
import { isToday } from '@/utils/isToday';

import { getDateStatus } from './helper';

import s from './CalendarDate.module.scss';

type CalendarDateProps = {
  day: DateTime<boolean>;
  isCurrentMonth: boolean;
  logs: WeeklyWorkLog[];
  drafts: DraftTimelog[];
  isDropDisabled?: boolean;
};

const MAX_VISIBLE_LOGS = 4;

const CalendarDate: React.FC<CalendarDateProps> = observer(
  ({ day, logs, drafts, isDropDisabled = false }) => {
    const { isOpen, selectedLog, showDetails, hideDetails, handleExited } = useDetailsDrawer();
    const { draftFilter } = useDraftTimelogStore();
    const { calendarExpandAll } = viewSettingsStore;
    const [isExpanded, setIsExpanded] = useState(false);

    const formatDay = day.day;
    const { selectedUser } = useGlobalUserFilterModel();
    const { user } = useUserStore();
    const { workTime } = viewSettingsStore;
    const myselfWorkTime = selectedUser?.uid === user?.id ? Number(workTime) : 40;
    const logMinutesDay =
      draftFilter !== 'drafts' ? logs.reduce((sum, log) => sum + log.durationMinutes, 0) : 0;
    const draftMinutesDay =
      draftFilter !== 'logs' ? drafts.reduce((sum, draft) => sum + draft.durationMinutes, 0) : 0;
    const dateTime = logMinutesDay + draftMinutesDay;

    const dayTimeTooltip =
      draftFilter === 'all' && draftMinutesDay > 0
        ? `Залогировано: ${formatDurationRussian(logMinutesDay)} · Черновики: ${formatDurationRussian(draftMinutesDay)}`
        : '';

    const visibleLogs = useMemo(() => logs.slice(0, MAX_VISIBLE_LOGS), [logs]);
    const hiddenLogs = useMemo(() => logs.slice(MAX_VISIBLE_LOGS), [logs]);
    const hasMoreLogs = logs.length > MAX_VISIBLE_LOGS;

    useEffect(() => {
      if (hasMoreLogs) {
        setIsExpanded(calendarExpandAll);
      }
    }, [calendarExpandAll, hasMoreLogs]);

    const onAddLogClick = (date: DateTime) => {
      modalManagerStore.open('addLog', { date, hasPreselectedDate: true });
    };

    const toggleExpand = () => {
      setIsExpanded((prev) => !prev);
    };

    const getButtonText = () => {
      if (isExpanded) {
        return 'свернуть';
      }

      return `показать (${hiddenLogs.length})`;
    };

    const droppableId = day.toISODate() || '';

    return (
      <div className={s.date}>
        <div className={cn(s.header, { [s.is_today]: isToday(day) })}>
          <p>{formatDay}</p>
          {dayTimeTooltip ? (
            <Tooltip title={dayTimeTooltip} arrow>
              <div
                className={cn(s.header__time, { [s.header__time_today]: isToday(day) })}
                style={{ backgroundColor: getDateStatus(dateTime, myselfWorkTime) }}
              >
                <span>{dateTime > 0 ? formatDurationRussian(dateTime) : '0ч'}</span>
              </div>
            </Tooltip>
          ) : (
            <div
              className={cn(s.header__time, { [s.header__time_today]: isToday(day) })}
              style={{ backgroundColor: getDateStatus(dateTime, myselfWorkTime) }}
            >
              <span>{dateTime > 0 ? formatDurationRussian(dateTime) : '0ч'}</span>
            </div>
          )}
        </div>
        <Droppable droppableId={droppableId} isDropDisabled={isDropDisabled}>
          {(provided, snapshot) => (
            <div
              className={cn(s.content, { [s.content_dragging]: snapshot.isDraggingOver })}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {draftFilter !== 'drafts' &&
                visibleLogs.map((log) => (
                  <LogItem key={log.id} log={log} onOpenDetails={showDetails} />
                ))}

              {draftFilter !== 'drafts' && hasMoreLogs && (
                <>
                  <Collapsed opened={isExpanded} className={s.collapsed}>
                    {hiddenLogs.map((log) => (
                      <LogItem key={log.id} log={log} onOpenDetails={showDetails} />
                    ))}
                  </Collapsed>
                  <Button
                    onClick={toggleExpand}
                    variant="text"
                    className={s.expandButton}
                    sx={{
                      fontSize: '12px',
                      textTransform: 'none',
                    }}
                  >
                    <ExpandMore
                      className={cn(s.expandIcon, { [s.expandIcon_rotated]: isExpanded })}
                    />
                    <span className={s.expandButtonText}>{getButtonText()}</span>
                  </Button>
                </>
              )}

              {draftFilter !== 'logs' &&
                drafts.map((draft) => <DraftLogItem key={draft.id} draft={draft} />)}

              <ReadonlyGuard>
                <Button onClick={() => onAddLogClick(day)} className={s.add} variant="text">
                  <Add sx={{ widh: '18px' }} />
                </Button>
              </ReadonlyGuard>
            </div>
          )}
        </Droppable>
        <DetailsDrawer
          open={isOpen}
          onClose={hideDetails}
          log={selectedLog}
          onExited={handleExited}
        />
      </div>
    );
  },
);

export default CalendarDate;
